/**
 * The campaign's event layer.
 *
 * Everything goes to `window.dataLayer`; nothing in the app imports gtag or
 * GA directly. That keeps the vendor swappable and gives one place to
 * enforce the rule below.
 *
 * WHAT MUST NEVER LEAVE THIS FILE
 * -------------------------------
 * The brief text, the visitor's email, the session token, and story
 * headlines. The session token in particular is a BEARER CREDENTIAL — it is
 * the whole of a campaign visitor's identity, and putting it in a URL or an
 * analytics payload hands their workspace to a third party. `scrub()`
 * enforces this structurally rather than by everyone remembering, and
 * `campaignAnalytics.test.js` fails if the guard is weakened.
 */

import { isConfigured, pushToDataLayer } from './gtm';
import { ensureCampaignTag } from './campaignTag';
import { createScrub, MAX_STRING } from './scrub';
import { resolveVariant } from '../features/Campaign/campaignVariant';

/**
 * The `/c/` funnel's policy: no arrays, no nested data, 64-char cap.
 *
 * The rules themselves now live in `scrub.js`, shared with the `/lp/` pages,
 * which need a slightly different policy. This export keeps the public surface
 * identical so `campaignAnalytics.test.js` still guards it unchanged.
 */
export const scrub = createScrub();

/**
 * §1 — attribution is recorded, never used to choose the copy.
 *
 * Read on EVERY event rather than captured once at submit. The funnel used
 * to read utm only inside `submitBrief`, so a visitor who landed from an ad
 * and left without typing anything produced no record at all — which is
 * precisely the number a paid campaign needs most.
 */
export function readUtmParams(search) {
  const out = {};
  try {
    const q = new URLSearchParams(
      search !== undefined ? search : (typeof window === 'undefined' ? '' : window.location.search),
    );
    for (const name of ['utm_campaign', 'utm_source', 'utm_medium', 'utm_content', 'utm_term', 'utm_creative']) {
      const v = q.get(name);
      if (v) out[name] = v.slice(0, MAX_STRING);
    }
  } catch {
    /* A malformed query string is not worth losing the event over. */
  }
  return out;
}

export function track(event, params = {}) {
  if (!isConfigured()) return null;
  /*
   * Boot the container HERE, immediately before the first push.
   *
   * The six trial pages were fully instrumented and measured nothing: they
   * push through this function, and nothing on those routes ever loaded the
   * container, so every `full_feed_opened`, `story_opened` and
   * `ai_draft_generated` went into a `dataLayer` array no tag ever read and
   * died with the tab. Verified on production 3 November 2026: the landing
   * page served one GTM script, `/trial/feed/<token>` opened cold from an
   * email served none.
   *
   * Not an effect in `TrialShell`, for the reason `campaignTag.js` sets out:
   * React runs CHILD effects before PARENT effects, and the pages fire their
   * first event from their own mount effects, so a parent-effect boot would
   * put the event into the array ahead of the Consent Mode defaults, and GTM
   * replays the array in order. A default that arrives after an event never
   * applied to that event.
   *
   * Here it is unconditionally correct: `ensureCampaignTag` is idempotent and
   * latched, and the consent default and any stored choice are pushed before
   * the event on the very first call, whichever page happens to call first.
   */
  ensureCampaignTag();
  // `campaign_variant` on EVERY event, not just the page view — comparing
  // two versions means segmenting conversions, and a conversion event that
  // does not carry the variant cannot be attributed to one.
  const payload = {
    event,
    ...scrub(params),
    ...readUtmParams(),
    campaign_variant: resolveVariant(),
  };
  pushToDataLayer(payload);
  return payload;
}

/**
 * The funnel is ONE route whose stage changes in local state, so without a
 * synthetic page view GA4 records a single page view per session and the
 * whole funnel is invisible. `campaign_stage` is the dimension to build the
 * funnel report on.
 */
export function trackStage(stage, audience) {
  if (!stage) return null;
  return track('campaign_page_view', { campaign_stage: stage, audience });
}

export const EVENTS = Object.freeze({
  BRIEF_SUBMIT: 'campaign_brief_submit',
  QUESTION_ANSWER: 'campaign_question_answer',
  QUESTION_SKIP: 'campaign_question_skip',
  LIST_READY: 'campaign_list_ready',
  LIST_EMPTY: 'campaign_list_empty',
  LIST_FAILED: 'campaign_list_failed',
  LIST_RETRY: 'campaign_list_retry',
  REPORT_REQUEST: 'campaign_report_request',
  REPORT_READY: 'campaign_report_ready',
  REPORT_FAILED: 'campaign_report_failed',
  REPORT_COPY: 'campaign_report_copy',
  ENROLL_SUBMIT: 'campaign_enroll_submit',
  ENROLL_SUCCESS: 'campaign_enroll_success',
  DEMO_REQUEST: 'campaign_demo_request',
  CTA_CLICK: 'campaign_cta_click',
  UPGRADE_PROMPT: 'campaign_upgrade_prompt',
  CONSENT: 'campaign_consent',

  /*
   * The trial flow, from the design handoff's analytics table (27 Oct 2026).
   *
   * Named exactly as the handoff names them, unprefixed, because they are the
   * events the report is specified against. The campaign_* events above stay as
   * they are: they describe the older /c/ funnel and renaming them would break
   * every existing report.
   *
   * `full_feed_opened` and `story_opened` are page LOADS, not clicks. The
   * handoff is explicit that a click does not prove the destination rendered,
   * which is the same argument `suggestionListViewService` makes on the backend
   * against tracking pixels.
   */
  FULL_FEED_OPENED: 'full_feed_opened',
  EMAIL_STORY_CLICKED: 'email_story_clicked',
  STORY_OPENED: 'story_opened',
  SOURCE_CLICKED: 'source_clicked',
  STORY_FEEDBACK_SUBMITTED: 'story_feedback_submitted',
  MORE_STORIES_REQUESTED: 'more_stories_requested',
  PREFERENCES_SAVED: 'preferences_saved',
  AI_DRAFT_REQUESTED: 'ai_draft_requested',
  AI_DRAFT_GENERATED: 'ai_draft_generated',
  AI_DRAFT_COPIED: 'ai_draft_copied',
  /* Not in the spec; the handoff offers them if the team wants them tracked. */
  CONTACT_CLICKED: 'contact_clicked',
  SITE_LINK_CLICKED: 'site_link_clicked',
});
